import { useEffect, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';

import type { UpdateNarrativeInput } from '../../hooks/useUpdatePublicNarrative';
import { useUpdatePublicNarrative } from '../../hooks/useUpdatePublicNarrative';
import { validateNarrativeForm } from '../../utils';

import { hasErrors, useNarrativeForm } from '../../hooks/useNarrativeForm';
import { useNarrativeErrorToast } from '../../hooks/useNarrativeErrorToast';
import type { NarrativeForm } from '../../utils/schema';
import type { Narrative } from '../../hooks/useNarrativeById';
import { htmlToText } from '@/components/RichTextEditor';
import { useNarrativeTracking } from '../../hooks/useNarrativeTracking';
import {
  NarrativeFormContent,
  useNarrativeFormHandler,
} from '../NarrativeFormContent';
import {
  narrativeGetValuatorQueryParamInitialValues,
  transformFormValuesToValuation,
} from '../../utils/transformation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { push } from 'redux-first-router';
import { usePastAnnualRevenueEarnings } from '../../hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import useUser from '@/hooks/useUser';
import NarrativeOnboardingModal from '@/components/NarrativeOnboardingModal';
import { merge } from 'lodash';
import {
  calculateValuationInitialisation,
  CreateNarrativeFuturePEMultipleCalculator,
  FuturePEMultipleContext,
  hasOnlyFairValue,
  useFuturePEMultipleCalculation,
  ViewEditNarrativeFuturePEMultipleCalculator,
} from '../Calculators';
import { useNarrativeInvalidate } from '../../hooks/useNarrativeInvalidate';

type Props = {
  narrative: Narrative;
};

export const PublicNarrativeEdit = ({ narrative }: Props) => {
  const {
    id: narrativeId,
    title: initialTitle,
    contentBlocks,
    valuation,
    company,
    userHoldsPosition,
    owner,
  } = narrative;

  if (!company) throw new Error("Narrative's company is missing");

  const { data: user } = useUser();

  const [showingOnboarding, setShowingOnboarding] = useState(
    !Boolean(user.displayName)
  );

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: valuation?.fiscalData,
  });

  const strategyContext = useMemo(
    () =>
      new FuturePEMultipleContext(
        hasOnlyFairValue(valuation)
          ? new CreateNarrativeFuturePEMultipleCalculator(company)
          : new ViewEditNarrativeFuturePEMultipleCalculator(valuation)
      ),
    [company, valuation]
  );

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
  } = useFuturePEMultipleCalculation(strategyContext);

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'public-narrative',
    companyId: company.id,
  });

  const { returnPath, goBack } = useNarrativeBack();
  const { invalidate } = useNarrativeInvalidate();

  useEffect(() => {
    if (!showingOnboarding && !user?.displayName) {
      goBack();
    }
  }, [showingOnboarding, user?.displayName, goBack]);

  const { mutate: updateNarrative, status: updateNarrativeStatus } =
    useUpdatePublicNarrative({
      onSuccess: () => {
        const path = getPath({
          type: ROUTE_PUBLIC_NARRATIVE_EDIT,
          payload: { id: narrativeId },
          query: returnPath
            ? {
                company: company.id,
                returnPath,
              }
            : {},
        });

        invalidate();
        trackEvent({
          action: 'save',
        });

        push(path);
        goBack();
      },
    });

  const initialContent = contentBlocks?.[0]?.contentHtml ?? '';

  const initialContentText = useMemo(
    () => htmlToText(initialContent),
    [initialContent]
  );

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = calculateValuationInitialisation({
      valuation,
      calculateFairValueFromInitialisation,
      calculateRevenueGrowthRateFromFairValue,
    });

    return merge(
      {
        ...calculatorOutputs,
        userHoldsPosition: userHoldsPosition ?? false,
        title: initialTitle ?? '',
        content: {
          text: initialContentText,
          html: initialContent,
        },
      },
      narrativeGetValuatorQueryParamInitialValues()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    valuation,
    userHoldsPosition,
    initialTitle,
    initialContentText,
    initialContent,
  ]);

  const {
    values,
    setValues,
    setPartialValues,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    resetForm,
  } = useNarrativeForm({
    initialValues: initialNarrativeForm,
    validate: validateNarrativeForm,
    enableReinitialize: true,
  });

  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({ trackEvent, dirty, errors });

  useEffect(() => {
    setValues(initialNarrativeForm);
    trackEvent({
      action: 'landed',
      narrativeId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrativeId]);

  const handleSubmit = () => {
    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    const { title, content, userHoldsPosition } = values;
    const { id, companyId } = narrative;

    const payload: UpdateNarrativeInput = {
      id,
      title,
      companyId,
      userHoldsPosition,
      contentBlocks: content.html
        ? [
            {
              contentHtml: content.html,
              // TODO Deprecate title
              title: contentBlocks?.[0]?.title || 'user_defined',
            },
          ]
        : [],
      valuations: [
        transformFormValuesToValuation({
          values,
        }),
      ],
    };
    updateNarrative({ input: payload });
    setSubmitCount(0);
  };

  const {
    handleSetPartialValues,
    handleSetFairValue,
    handleContentChange,
    handleHeaderChange,
  } = useNarrativeFormHandler({
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    setPartialValues,
    values,
  });

  if (showingOnboarding)
    return (
      <Track>
        <NarrativeOnboardingModal
          companyId={company.id}
          isOpen={showingOnboarding}
          onClose={() => setShowingOnboarding(false)}
        />
      </Track>
    );

  const isLoading = updateNarrativeStatus === 'pending';

  return (
    <Track>
      <NarrativeFormContent
        errors={errors}
        values={values}
        onChange={handleSetPartialValues}
        onSetFairValue={handleSetFairValue}
        onContentChange={handleContentChange}
        onHeaderChange={handleHeaderChange}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitLabel="Update"
        company={company}
        initialContentHtml={initialNarrativeForm.content.html}
        owner={owner}
        onReset={resetForm}
        pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
        calculationBreakDownOutput={calculateBreakDown({
          discountRatePercentage: values.discountRatePercentage,
          futurePE: values.priceEarningsMultiple,
          profitMarginPercentage: values.profitMarginPercentage,
          revenueGrowthRatePercentage: values.revenuePercentage,
          timeFrame: values.timePeriod,
        })}
      />
    </Track>
  );
};
